import { useState } from "react";
import PropTypes from "prop-types";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import { useRunner } from "components/admin/hooks";
import api from "api/client";

const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghijkmnpqrstuvwxyz";
const DIGITS = "23456789";

function pick(chars) {
  const value = new Uint32Array(1);
  window.crypto.getRandomValues(value);
  return chars[value[0] % chars.length];
}

/** Luôn có chữ hoa, chữ thường và số để qua luật mật khẩu của game. */
export function randomStrongPassword(length = 12) {
  const chars = [pick(UPPER), pick(LOWER), pick(DIGITS)];
  while (chars.length < length) chars.push(pick(UPPER + LOWER + DIGITS));
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

/** Cùng luật với Player.IsStrongPassword ở máy chủ. */
export function passwordError(password) {
  if (!password) return null;
  if (password.length < 8 || password.length > 59) return "Mật khẩu 8 - 59 ký tự";
  if (/[^\x21-\x7e]/.test(password)) return "Không dùng dấu cách hoặc chữ có dấu";
  if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[^A-Za-z]/.test(password))
    return "Cần có chữ hoa, chữ thường và số hoặc ký tự đặc biệt";
  return null;
}

// Giống Player.NormalizeCharName / IsValidCharName ở server: ghép dấu (NFC), bỏ khoảng trắng thừa;
// cho tiếng Việt có dấu, chữ hoa, ký tự đặc biệt; chặn emoji (cột `player`.`name` là utf8 3 byte)
// và ký tự điều khiển / vô hình.
export function normalizeCharacterName(name) {
  return (name || "").normalize("NFC").trim().split(/\s+/).filter(Boolean).join(" ");
}

export function characterNameError(name) {
  const n = normalizeCharacterName(name);
  if (!n) return null;
  if (n.length < 5 || n.length > 35) return "Dài 5 - 35 ký tự";
  if (/[\u{10000}-\u{10FFFF}\p{C}\p{Mn}\p{Me}\p{Z}]/u.test(n.replace(/ /g, "")))
    return "Không dùng emoji hoặc ký tự điều khiển";
  return null;
}

export const EMPTY_ACCOUNT = { password: "", characterName: "", gender: 0 };

/** Ô mật khẩu, tên nhân vật, giới tính khi tạo tài khoản mới. */
export function NewAccountFields({ value, onChange }) {
  const pwError = passwordError(value.password);
  const nameError = characterNameError(value.characterName);
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <MDInput
          fullWidth
          label="Mật khẩu (để trống = tạo ngẫu nhiên)"
          value={value.password}
          error={!!pwError}
          helperText={pwError || "8 - 59 ký tự, có chữ hoa, chữ thường và số"}
          autoComplete="new-password"
          onChange={(e) => onChange({ ...value, password: e.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Tạo ngẫu nhiên">
                  <IconButton
                    size="small"
                    onClick={() => onChange({ ...value, password: randomStrongPassword() })}
                  >
                    <Icon>casino</Icon>
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <MDInput
          fullWidth
          label="Tên nhân vật (để trống = tự tạo khi vào game)"
          value={value.characterName}
          error={!!nameError}
          helperText={nameError || "5 - 35 ký tự, được dùng tiếng Việt có dấu, ký tự đặc biệt"}
          onChange={(e) => onChange({ ...value, characterName: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <MDInput
          select
          fullWidth
          label="Giới tính"
          value={value.gender}
          disabled={!value.characterName}
          onChange={(e) => onChange({ ...value, gender: Number(e.target.value) })}
          SelectProps={{ sx: { height: 44 } }}
        >
          <MenuItem value={0}>Nam</MenuItem>
          <MenuItem value={1}>Nữ</MenuItem>
        </MDInput>
      </Grid>
    </Grid>
  );
}

NewAccountFields.propTypes = {
  value: PropTypes.shape({
    password: PropTypes.string,
    characterName: PropTypes.string,
    gender: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export function isNewAccountValid(value) {
  return !passwordError(value.password) && !characterNameError(value.characterName);
}

function CopyLine({ label, text }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      <MDTypography variant="button" color="text" minWidth={90}>
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="bold" sx={{ fontFamily: "monospace" }}>
        {text}
      </MDTypography>
      <Tooltip title="Sao chép">
        <IconButton size="small" onClick={() => navigator.clipboard?.writeText(text)}>
          <Icon fontSize="small">content_copy</Icon>
        </IconButton>
      </Tooltip>
    </MDBox>
  );
}

CopyLine.propTypes = { label: PropTypes.string.isRequired, text: PropTypes.string.isRequired };

/** Thông tin đăng nhập vừa tạo, chỉ hiện một lần vì máy chủ chỉ lưu mật khẩu đã băm. */
export function CreatedAccountAlert({ account, onClose }) {
  return (
    <Alert severity="success" onClose={onClose} sx={{ mb: 2 }}>
      <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
        Đã tạo tài khoản. Lưu lại mật khẩu ngay, đóng thông báo này là không xem lại được.
      </MDTypography>
      <CopyLine label="Tài khoản" text={account.username} />
      <CopyLine label="Mật khẩu" text={account.password} />
      {account.characterName && <CopyLine label="Nhân vật" text={account.characterName} />}
      {account.permissions?.length > 0 && (
        <MDTypography variant="caption" color="text" display="block">
          Đăng nhập web admin được với {account.permissions.length} quyền đã chọn.
        </MDTypography>
      )}
    </Alert>
  );
}

CreatedAccountAlert.propTypes = {
  account: PropTypes.shape({
    username: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    characterName: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export function createAccount(body) {
  return api.post("/api/admin/accounts", body);
}

/** Tạo tài khoản game (đăng nhập được game), không cấp quyền web admin. */
export function CreateAccountDialog({ onClose, onCreated }) {
  const [run, busy] = useRunner();
  const [username, setUsername] = useState("");
  const [account, setAccount] = useState(EMPTY_ACCOUNT);
  const submit = async () => {
    const { ok, result } = await run(
      () => createAccount({ username: username.trim(), ...account }),
      (data) => `Đã tạo tài khoản ${data.username}`
    );
    if (ok) {
      onClose();
      onCreated(result);
    }
  };
  return (
    <ActionDialog
      open
      title="Tạo tài khoản game"
      submitText="Tạo tài khoản"
      maxWidth="md"
      busy={busy}
      disabled={!username.trim() || !isNewAccountValid(account)}
      onClose={onClose}
      onSubmit={submit}
    >
      <MDInput
        label="Tên đăng nhập (tối đa 20 ký tự)"
        value={username}
        inputProps={{ maxLength: 20 }}
        onChange={(e) => setUsername(e.target.value)}
        autoFocus
        autoComplete="off"
      />
      <NewAccountFields value={account} onChange={setAccount} />
      <Alert severity="info">
        Tài khoản thường, đăng nhập được game, không vào được web admin. Muốn cho vào web admin thì
        tạo ở trang Phân quyền.
      </Alert>
    </ActionDialog>
  );
}

CreateAccountDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};
